// Guard: this module holds per-tab mutable state (`pendingIntent`). In a
// long-lived Node/server process that state would be shared across all
// requests/users, so importing it server-side is a bug. `client-only` makes
// such an import fail the build (mirrors `server-only` used elsewhere in this
// package). No-op in the browser bundle and in unit tests.
import 'client-only';

/**
 * @name auth-intent
 * @description Records the *intent* behind a programmatic sign-out so the
 * auth-change listener can tell an app-driven `SIGNED_OUT` apart from a
 * spurious/forced one (e.g. a token-refresh race against the server
 * middleware — PAY-1006).
 *
 * Two intents:
 * - `navigate` — the user clicked "sign out". The listener sends them to the
 *   marketing home.
 * - `silent` — a programmatic sign-out that is immediately followed by a new
 *   session (admin impersonation: `signOut()` → `setSession()` → its own
 *   `replace('/home')`). The listener must do NOTHING so it doesn't race the
 *   flow's own navigation.
 *
 * A `SIGNED_OUT` with no recorded intent is treated as forced/spurious.
 *
 * **Cross-tab:** supabase-js broadcasts `SIGNED_OUT` to every open tab, but the
 * module-level flag below is per-tab. So we mirror the user-initiated
 * `navigate` intent into `localStorage` (shared per-origin) and fall back to it
 * on read. A non-initiating tab that receives the broadcast therefore
 * recognises the sign-out as app-initiated: it navigates to `/` like the
 * initiating tab and does NOT take the forced branch (no `auth.forced_sign_out`
 * telemetry and no `?next=` sign-in redirect). That is the intended multi-tab
 * sign-out behaviour (PAY-1006).
 *
 * The `silent` (impersonation) intent is NEVER shared — it is single-tab by
 * design, so other tabs handle the broadcast normally (forced → redirect to
 * sign-in) rather than skip navigation and strand the user on a private route.
 * The shared entry is TTL-gated so a stale value can't misclassify a genuine
 * forced sign-out that happens later.
 *
 * The same-tab flag is the fast, authoritative path; it's one-shot and not
 * persisted — losing it on a hard reload is fine.
 */
export type SignOutIntent = 'navigate' | 'silent';

let pendingIntent: SignOutIntent | null = null;

const SHARED_KEY = 'ci.auth.signout-intent';
// Kept short on purpose. It only needs to outlive the gap between the
// initiating tab writing the intent and other tabs reading it off the
// near-instant SIGNED_OUT broadcast (tens of ms). A SIGNED_OUT can't be
// correlated to a specific intent, so this window is also how long a stale
// intent could mask a later, unrelated forced sign-out — keep it just long
// enough for cross-tab delivery to minimise that (PAY-1006).
const SHARED_TTL_MS = 1_500;

// Only the user-initiated 'navigate' intent is shared cross-tab; 'silent'
// (impersonation) is single-tab by design (see markSilentSignOut).
function writeShared(intent: 'navigate') {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      SHARED_KEY,
      JSON.stringify({ intent, at: Date.now() }),
    );
  } catch {
    // localStorage may be unavailable (private mode / quota). The same-tab flag
    // still works; only the cross-tab signal is lost.
  }
}

function removeShared() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(SHARED_KEY);
  } catch {
    // ignore
  }
}

function readShared(): SignOutIntent | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SHARED_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { intent?: unknown; at?: unknown };

    // Only 'navigate' is a valid cross-tab intent (silent is single-tab). This
    // also defends against a stale value written by an earlier build.
    if (
      parsed.intent === 'navigate' &&
      typeof parsed.at === 'number' &&
      Date.now() - parsed.at <= SHARED_TTL_MS
    ) {
      return parsed.intent;
    }
  } catch {
    // malformed / unavailable storage → treat as no intent
  }

  return null;
}

/**
 * @name markAppInitiatedSignOut
 * @description Record that the user initiated the next sign-out (→ navigate to
 * the marketing home). Call immediately before `supabase.auth.signOut()`.
 */
export function markAppInitiatedSignOut() {
  pendingIntent = 'navigate';
  writeShared('navigate');
}

/**
 * @name markSilentSignOut
 * @description Record that the next sign-out is part of a sign-out→sign-in
 * transition (impersonation) and the listener should not navigate. Call
 * immediately before `supabase.auth.signOut()`.
 */
export function markSilentSignOut() {
  // Per-tab ONLY — deliberately NOT mirrored to shared storage. Impersonation
  // runs in a single tab; only that tab should skip navigation on the resulting
  // SIGNED_OUT. Other tabs receive Supabase's SIGNED_OUT broadcast and must
  // handle it normally (forced → redirect to sign-in) rather than silently
  // doing nothing and stranding the user on a private route with no session
  // (PAY-1006).
  pendingIntent = 'silent';
}

/**
 * @name clearSignOutIntent
 * @description Clear any recorded intent (this tab's flag and the shared
 * entry) without reading it. Call this if `signOut()` fails to complete so a
 * set-but-unconsumed intent can't leak to a later, unrelated `SIGNED_OUT`.
 */
export function clearSignOutIntent() {
  pendingIntent = null;
  removeShared();
}

/**
 * @name consumeSignOutIntent
 * @description Read the recorded sign-out intent. Returns the intent for an
 * app-driven sign-out, or `null` for a forced/spurious one.
 *
 * The same-tab flag is one-shot (cleared on read). When it's absent — e.g. this
 * tab received a `SIGNED_OUT` broadcast from another tab — we fall back to the
 * TTL-gated shared entry, which is intentionally NOT cleared on read so every
 * other tab can still see it within the window.
 */
export function consumeSignOutIntent(): SignOutIntent | null {
  if (pendingIntent) {
    const value = pendingIntent;
    pendingIntent = null;

    return value;
  }

  return readShared();
}
